/**
 * Desktop mode changes for focus over the wall (Part 05 decision 8, Q10).
 * Closing the Workspace while an item is open above the wall carries focus
 * over: it floats over the desktop as compact focus, and Back returns to the
 * wall. Review and deliverables cannot be drawn in compact, so they end.
 *
 * The other rows keep their existing owners: opening the Workspace ends
 * compact focus and leaving peek leaves focus alone (`canvas-presentation.ts`).
 */
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { isTauri } from "@/lib/is-tauri";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { usePresentationStore } from "@/stores/presentation-store";

type DesktopModeState = ReturnType<typeof useDesktopMode.getState>;

function carryFocusOverToCompact(
  state: DesktopModeState,
  prev: DesktopModeState,
): void {
  if (!isTauri() || prev.mode !== "denk" || state.mode !== "compact") return;
  // A focus entry already named the frames that float.
  if (state.compactFrameOverlayIds.length > 0) return;
  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.renderer !== "overlay") return;
  const { displayId } = presentation;
  if (displayId != null && displayId !== getWindowDisplayId()) return;
  if (presentation.variant !== "focus") {
    exitPresentationMode();
    return;
  }
  state.closeWorkspace(presentation.frameIds, { returnToWorkspace: true });
}

/** Follow this window's desktop mode; returns the unsubscribe. */
export function startFocusModeTransitions(): () => void {
  return useDesktopMode.subscribe(carryFocusOverToCompact);
}
